/**
 * Find available slot ID by start/end time
 * @param {Object} data - The API response object
 * @param {Date} startTime - Start time as a Date object
 * @param {Date} endTime - End time as a Date object
 * @returns {string|null} - Matching slot ID if found and available, otherwise null
 */
const findAvailableSlotId = (data, startTime, endTime) => {
  // Find the day that matches the startTime date
  const matchingDay = data.find((day) => {
    const dayDate = new Date(day.date);
    return (
      dayDate.getFullYear() === startTime.getFullYear()
      && dayDate.getMonth() === startTime.getMonth()
      && dayDate.getDate() === startTime.getDate()
    );
  });

  if (!matchingDay) return null;

  //  Find matching slot inside that day
  const matchingSlot = matchingDay.slots.find((slot) => {
    const slotStart = new Date(slot.startTime);
    const slotEnd = new Date(slot.endTime);

    return (
      slot.isAvailable
      && slotStart.getTime() === startTime.getTime()
      && slotEnd.getTime() === endTime.getTime()
    );
  });

  return matchingSlot ? matchingSlot.id : null;
};

/**
 * Formats a given Date object to a string in "HH:mm:00" format using UTC time.
 *
 * @param {Date} date - The Date object to format.
 * @returns {string} The formatted time string in "HH:mm:00" format (UTC).
 */
const formatTime = (date) => {
  const hrs = date.getHours().toString().padStart(2, '0');
  const mins = date.getMinutes().toString().padStart(2, '0');
  return `${hrs}:${mins}:00`;
};

/**
 * Determines if two Date objects represent the same calendar day
 *
 * @param {Date} a - The first date to compare.
 * @param {Date} b - The second date to compare.
 * @returns {boolean} True if both dates are on the same day, otherwise false.
 */
const areSameDay = (a, b) => a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

/**
 * Combines a date object and a time string into a single Date object.
 *
 * @param {Date} date - The date to combine with the time
 * @param {string} time - The time to set, in "HH:mm" format (24-hour).
 * @returns {Date} A new Date object with the specified date and time.
 */
const combineDateAndTime = (date, time) => {
  // Ensure we don't mutate the original date
  const result = new Date(date);

  const [hours, minutes] = time.split(':').map(Number);
  result.setUTCHours(hours, minutes, 0, 0);

  return result;
};

/**
 * Get available and unavailable times for a specific day.
 *
 * @param {Array} slots - Array of date-slot objects
 * @param {Date} d - The date object to match (day only)
 * @param {Date} startTime - Start time of the range
 * @param {Date} endTime - End time of the range (inclusive)
 * @param {string} interval - Time interval, format "HH:MM"
 * @returns {{ available: string[], unavailable: string[] }}
 */
function getDaySlots(slots, d, startTime, endTime, interval) {
  const [hourStep, minuteStep] = interval.split(':').map(Number);
  const available = [];
  const unavailable = [];
  const current = new Date(combineDateAndTime(d, startTime));
  const endDateTime = combineDateAndTime(d, endTime);

  // find matching day
  const day = slots.find((item) => areSameDay(new Date(item.date), d));
  if (!day) return { available, unavailable };

  while (current <= endDateTime) {
    const currentTime = current.getTime();
    // eslint-disable-next-line max-len
    const matchingSlot = day.slots.find((slot) => new Date(slot.startTime).getTime() === currentTime);

    if (matchingSlot && matchingSlot.isAvailable) {
      available.push(formatTime(current));
    } else {
      unavailable.push(formatTime(current));
    }

    // move to next interval
    current.setHours(current.getHours() + hourStep);
    current.setMinutes(current.getMinutes() + minuteStep);
  }

  return { available, unavailable };
}

/**
 * Get available time slots for TimeGrid component from slots data.
 * Extracts only the available times from the slots for a specific date.
 *
 * @param {Array} slots - Array of date-slot objects
 * @param {Date} d - The date object to match (day only)
 * @returns {string[]} Array of time strings in "HH:mm" format
 */
function getAvailableTimesFromSlots(slots, d) {
  // find matching day
  const day = slots.find((item) => areSameDay(new Date(item.date), d));
  if (!day || !day.slots) return [];

  // Extract available times from slots and format them
  const availableTimes = day.slots
    .filter((slot) => slot.isAvailable)
    .map((slot) => {
      const slotDate = new Date(slot.startTime);
      // Format as "HH:mm" for TimeGrid
      const hours = slotDate.getHours().toString().padStart(2, '0');
      const minutes = slotDate.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    })
    .sort((a, b) => {
      // Sort times chronologically by comparing hour and minute
      const [hoursA, minutesA] = a.split(':').map(Number);
      const [hoursB, minutesB] = b.split(':').map(Number);
      if (hoursA !== hoursB) return hoursA - hoursB;
      return minutesA - minutesB;
    });

  return availableTimes;
}

export {
  findAvailableSlotId,
  getDaySlots,
  getAvailableTimesFromSlots,
};
